import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building, Users, Settings, Plus, Eye, Edit, BarChart3 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CreateOrganizationModal } from '@/components/CreateOrganizationModal';
import { CreateWorkspaceModal } from '@/components/CreateWorkspaceModal';
import { EditWorkspaceModal } from '@/components/multi-tenant/EditWorkspaceModal';
import { ManageMembersPanel } from '@/components/multi-tenant/ManageMembersPanel';
import { ModuleManagementModal } from '@/components/multi-tenant/ModuleManagementModal';
import { WorkspaceViewModal } from '@/components/multi-tenant/WorkspaceViewModal';
import { WorkspaceManagementTabs } from '@/components/workspace/WorkspaceManagementTabs';
import { TrialStatusBanner } from '@/components/TrialStatusBanner';
import { useNavigate } from 'react-router-dom';

// Real data hooks
const useMultiTenantData = () => {
  // Fetch organizations with stats
  const orgsQuery = useQuery({
    queryKey: ['multi-tenant-orgs'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('orgs-manage', {
        body: { action: 'list_orgs', limit: 100 }
      });
      if (error) throw error;
      return data.organizations;
    }
  });

  // Fetch workspaces with stats  
  const workspacesQuery = useQuery({
    queryKey: ['multi-tenant-workspaces'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workspaces-manage', {
        body: { action: 'list_workspaces', limit: 100 }
      });
      if (error) throw error;
      return data.workspaces;
    }
  });

  // Fetch high-level stats
  const statsQuery = useQuery({
    queryKey: ['multi-tenant-stats'],
    queryFn: async () => {
      const [orgsStats, workspacesStats] = await Promise.all([
        supabase.functions.invoke('orgs-manage', { body: { action: 'stats' } }),
        supabase.functions.invoke('workspaces-manage', { body: { action: 'stats' } })
      ]);
      
      if (orgsStats.error) throw orgsStats.error;
      if (workspacesStats.error) throw workspacesStats.error;
      
      return {
        organizations: orgsStats.data.organizations,
        workspaces: orgsStats.data.workspaces,
        users: orgsStats.data.users,
        enterprise_orgs: orgsStats.data.enterprise_orgs,
        workspaceStats: workspacesStats.data
      };
    }
  });

  return {
    organizations: orgsQuery.data || [],
    workspaces: workspacesQuery.data || [], 
    stats: statsQuery.data || { organizations: 0, workspaces: 0, users: 0, enterprise_orgs: 0 },
    isLoading: orgsQuery.isLoading || workspacesQuery.isLoading || statsQuery.isLoading,
    refetch: () => {
      orgsQuery.refetch();
      workspacesQuery.refetch();
      statsQuery.refetch();
    }
  };
};

const getPlanColor = (plan: string) => {
  switch (plan) {
    case 'Enterprise':
      return 'bg-purple-100 text-purple-800';
    case 'Pro':
      return 'bg-blue-100 text-blue-800';
    case 'Basic':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'trial':
      return 'bg-yellow-100 text-yellow-800';
    case 'suspended':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function MultiTenant() {
  const navigate = useNavigate();
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [viewMode, setViewMode] = useState<'organizations' | 'workspaces'>('organizations');
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [showEditWorkspace, setShowEditWorkspace] = useState(false);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [showModuleManagement, setShowModuleManagement] = useState(false);
  const [showWorkspaceView, setShowWorkspaceView] = useState(false);

  // Get real data
  const { organizations, workspaces, stats, isLoading, refetch } = useMultiTenantData();

  const filteredWorkspaces = selectedOrg 
    ? workspaces.filter(w => w.organization?.id === selectedOrg)
    : workspaces;

  const handleCreateOrgSuccess = (orgId: string) => {
    refetch();
    // Optionally navigate to org detail or show workspace creation
    setIsCreateWorkspaceModalOpen(true);
  };

  const handleCreateWorkspaceSuccess = (workspaceId: string) => {
    refetch();
    navigate(`/w/${workspaceId}/dashboard`);
  };

  const handleViewWorkspace = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setShowWorkspaceView(true);
  };

  const handleEditWorkspace = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setShowEditWorkspace(true);
  };

  const handleManageMembers = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setShowManageMembers(true);
  };

  const handleManageModules = (workspace: any) => {
    setSelectedWorkspace(workspace);
    setShowModuleManagement(true);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Multi-Tenant Management</h1>
            <p className="text-muted-foreground">
              Manage organizations, workspaces, and tenant isolation
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setIsCreateOrgModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Organization
          </Button>
          <Button
            onClick={() => setIsCreateWorkspaceModalOpen(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Create Workspace
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizations</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.organizations}</div>
            <p className="text-xs text-muted-foreground">
              Platform total
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Workspaces</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.workspaces}</div>
            <p className="text-xs text-muted-foreground">
              Across all orgs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.users}</div>
            <p className="text-xs text-muted-foreground">
              Platform-wide
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enterprise Orgs</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.enterprise_orgs}
            </div>
            <p className="text-xs text-muted-foreground">
              High-tier customers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Multi-Tenant Overview</CardTitle>
              <CardDescription>
                Switch between organization and workspace views
              </CardDescription>
            </div>
            <div className="flex items-center space-x-4">
              <Select value={viewMode} onValueChange={(value: 'organizations' | 'workspaces') => setViewMode(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organizations">Organizations</SelectItem>
                  <SelectItem value="workspaces">Workspaces</SelectItem>
                </SelectContent>
              </Select>
              {viewMode === 'workspaces' && (
                <Select value={selectedOrg || 'all'} onValueChange={(value) => setSelectedOrg(value === 'all' ? undefined : value)}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Filter by organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'organizations' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Workspaces</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{org.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Created: {new Date(org.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPlanColor(org.type || 'Basic')}>
                        {org.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{org.workspaces?.length || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">-</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(org.status)}>
                        {org.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{org.primary_contact || '-'}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewWorkspace(org)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditWorkspace(org)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleManageMembers(org)}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkspaces.map((workspace) => (
                  <TableRow key={workspace.id}>
                    <TableCell className="font-medium">{workspace.name}</TableCell>
                    <TableCell>{workspace.organization?.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{workspace.members?.length || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPlanColor(workspace.plan?.name || 'Basic')}>
                        {workspace.plan?.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewWorkspace(workspace)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditWorkspace(workspace)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleManageMembers(workspace)}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleManageModules(workspace)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Tenant Isolation Status */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Isolation Status</CardTitle>
          <CardDescription>
            Monitor data isolation and security between tenants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Data Isolation</span>
                <Badge className="bg-green-100 text-green-800">Active</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Row-level security enforced across all tables
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Access Controls</span>
                <Badge className="bg-green-100 text-green-800">Enforced</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Workspace-based access restrictions active
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Audit Logging</span>
                <Badge className="bg-green-100 text-green-800">Enabled</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Cross-tenant access attempts logged
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={isCreateOrgModalOpen}
        onClose={() => setIsCreateOrgModalOpen(false)}
        onSuccess={handleCreateOrgSuccess}
      />

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={isCreateWorkspaceModalOpen}
        onClose={() => setIsCreateWorkspaceModalOpen(false)}
        onSuccess={handleCreateWorkspaceSuccess}
      />

      {/* Multi-Tenant Management Modals */}
      <EditWorkspaceModal
        workspace={selectedWorkspace}
        isOpen={showEditWorkspace}
        onClose={() => setShowEditWorkspace(false)}
        onUpdate={() => {
          setShowEditWorkspace(false);
          refetch();
        }}
      />

      <ManageMembersPanel
        workspace={selectedWorkspace}
        isOpen={showManageMembers}
        onClose={() => setShowManageMembers(false)}
      />

      <ModuleManagementModal
        workspace={selectedWorkspace}
        isOpen={showModuleManagement}
        onClose={() => setShowModuleManagement(false)}
      />

      <WorkspaceViewModal
        workspace={selectedWorkspace}
        isOpen={showWorkspaceView}
        onClose={() => setShowWorkspaceView(false)}
      />
    </div>
  );
}